import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TaskBadge from "@/components/TaskBadge";
import TaskFromCallDialog from "@/components/TaskFromCallDialog";
import {
  ClipboardPlus,
  HardDriveDownload,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Play,
} from "lucide-react";

const MISSED = ["missed", "no_answer", "busy", "failed"];
// An incoming call nobody answered.
export const isMissed = (c) => c.direction === "inbound" && MISSED.includes(c.status);
// An outgoing call that never connected (customer didn't pick up, line busy...).
const notConnected = (c) =>
  c.direction !== "inbound" && [...MISSED, "provider_not_configured"].includes(c.status);

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtDur(sec) {
  const s = Number(sec) || 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "missed", label: "Missed" },
  { key: "inbound", label: "Incoming" },
  { key: "outbound", label: "Outgoing" },
];

/**
 * The call log for owners and employees: missed calls in red, a Call button
 * on every row. Employees get numbers already masked by the server, and no
 * recordings. `owner` adds the agent name and recording links.
 */
export default function CallLogList({ calls, owner = false, onCalled, canCreateTask = false, selfId }) {
  const [filter, setFilter] = useState("all");
  const [calling, setCalling] = useState(null);
  const [taskFor, setTaskFor] = useState(null); // the call a new task is being made from

  const missedCount = calls.filter(isMissed).length;
  const shown = calls.filter((c) =>
    filter === "all"
      ? true
      : filter === "missed"
        ? isMissed(c)
        : filter === "inbound"
          ? c.direction === "inbound"
          : c.direction !== "inbound"
  );

  const callBack = async (c) => {
    if (calling) return;
    setCalling(c.id);
    try {
      const { data } = await api.post("/calls/initiate", { call_id: c.id });
      toast.success(data.message || "Your phone will ring in a few seconds.");
      onCalled?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the call.");
    } finally {
      setCalling(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" data-testid="calllog-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
              filter === f.key
                ? f.key === "missed"
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-slate-900 text-white border-slate-900"
                : f.key === "missed" && missedCount
                  ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-white text-slate-600 border-slate-200"
            }`}
            data-testid={`calllog-filter-${f.key}`}
          >
            {f.label}
            {f.key === "missed" && missedCount > 0 && ` (${missedCount})`}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="calllog-list">
        {shown.map((c) => {
          const missed = isMissed(c);
          const failed = notConnected(c);
          const Icon = missed
            ? PhoneMissed
            : failed
              ? PhoneOff
              : c.direction === "inbound"
                ? PhoneIncoming
                : PhoneOutgoing;
          return (
            <div
              key={c.id}
              className={`flex items-center gap-3 px-3 py-3 ${missed ? "bg-red-50/60" : ""}`}
              data-testid={`call-row-${c.id}`}
            >
              <div
                className={`w-9 h-9 shrink-0 rounded-full grid place-items-center ${
                  missed
                    ? "bg-red-100 text-red-600"
                    : failed
                      ? "bg-slate-100 text-slate-500"
                      : c.direction === "inbound"
                        ? "bg-blue-50 text-[#0055FF]"
                        : "bg-orange-50 text-[#FF5000]"
                }`}
              >
                <Icon className="w-4 h-4" />
              </div>

              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium truncate ${missed ? "text-red-600" : ""}`}>
                  {c.customer_name || "Unknown number"}
                </div>
                <div className={`mono text-[11px] truncate ${missed ? "text-red-500" : "text-slate-500"}`}>
                  <TaskBadge call={c} /> {c.customer_phone}
                </div>
                <div className="mono text-[10px] text-slate-400 truncate">
                  {fmtDate(c.started_at)}
                  {missed && <span className="text-red-600 font-semibold"> · Missed</span>}
                  {failed && " · Not connected"}
                  {owner && c.employee_name && !missed ? ` · ${c.employee_name}` : ""}
                  {owner && c.ivr_path ? ` · IVR ${c.ivr_path}` : ""}
                </div>
                {c.called_back_by && (
                  <div className="mono text-[10px] text-emerald-700 truncate">
                    ✓ Called back by {c.called_back_by}
                  </div>
                )}
                {canCreateTask && (
                  <button
                    type="button"
                    onClick={() => setTaskFor(c)}
                    className="mt-1 inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-50"
                    data-testid={`call-task-new-${c.id}`}
                  >
                    <ClipboardPlus className="w-3 h-3" /> Task
                  </button>
                )}
              </div>

              <div className="shrink-0 text-right">
                <div className="mono text-xs tabular-nums text-slate-600">
                  {missed || failed ? "—" : fmtDur(c.duration_sec)}
                </div>
                {owner && c.recording_url && (
                  <Link
                    to={`/dashboard/recordings?call=${c.id}`}
                    className="inline-flex items-center gap-0.5 text-[11px] text-[#0055FF] hover:underline"
                    data-testid={`call-listen-${c.id}`}
                  >
                    <Play className="w-3 h-3" /> Listen
                  </Link>
                )}
                {owner && !c.recording_url && c.recording_on_pc && (
                  <Link
                    to={`/dashboard/recordings?call=${c.id}`}
                    className="inline-flex items-center gap-0.5 text-[11px] text-amber-700 hover:underline"
                    title="Older recording kept on the recorder PC"
                    data-testid={`call-fetch-${c.id}`}
                  >
                    <HardDriveDownload className="w-3 h-3" /> PC
                  </Link>
                )}
              </div>

              <button
                type="button"
                onClick={() => callBack(c)}
                disabled={!!calling}
                className={`w-10 h-10 shrink-0 rounded-full grid place-items-center text-white disabled:opacity-50 ${
                  missed ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"
                }`}
                title="Call this number"
                aria-label={`Call ${c.customer_name || "this number"}`}
                data-testid={`call-back-${c.id}`}
              >
                <Phone className={`w-4 h-4 ${calling === c.id ? "animate-pulse" : ""}`} />
              </button>
            </div>
          );
        })}
        {shown.length === 0 && (
          <div className="text-sm text-slate-500 text-center p-6">
            {filter === "missed" ? "No missed calls." : "No calls yet."}
          </div>
        )}
      </div>
      <TaskFromCallDialog
        call={taskFor}
        owner={owner}
        selfId={selfId}
        onClose={() => setTaskFor(null)}
        onCreated={() => onCalled?.()}
      />
    </div>
  );
}
